import { fmt, type Column } from "@racoon/ui";
import { fromTimestamp, toTimestamp } from "@racoon/lib";
import { DateValue } from "~/shared/lib/analytics-utils";
import type { ReportDriverOnShift, ReportJob } from "./use-report-data";
import { compareIds } from "~/shared/lib/compare-ids";

const dayStart = (d: DateValue) => new Date(d.year, d.month - 1, d.day, 0, 0, 0, 0);
const dayEnd = (d: DateValue) => new Date(d.year, d.month - 1, d.day, 23, 59, 59, 999);

/** The report period as inclusive bounds on the browser's local days. */
export const periodBounds = (startDate: DateValue, endDate: DateValue) => ({
  from: toTimestamp(dayStart(startDate)),
  to: toTimestamp(dayEnd(endDate)),
});

/** "Surname Name", the order the reports write people in. */
export const personName = (user?: { firstName?: string; lastName?: string }) =>
  `${user?.lastName ?? ""} ${user?.firstName ?? ""}`.trim();

/** When a shift is placed: its planned window, else its start or creation (older shifts). */
export const shiftDate = (shift: ReportDriverOnShift) =>
  fromTimestamp(shift.twStartAt ?? shift.startedAt ?? shift.createdAt);

export { compareIds };

/** An id as a map key; "" when unset. */
export const idKey = (id?: bigint | null) => (id ? String(id) : "");

// Jobs Z as the admin writes them, sites PL as govdash does.
const ENTITY_PREFIX = {
  job: "Z",
  site: "PL",
  user: "U",
  containerGroup: "CG",
  truckType: "TT",
} as const;

/** The ID a report shows for an entity, e.g. "Z1024"; "—" when there is no entity. */
export const entityNumber = (kind: keyof typeof ENTITY_PREFIX, id?: bigint | string | null) =>
  id ? `${ENTITY_PREFIX[kind]}${id}` : "—";

export const reportDate = (date: Date | null | undefined) => (date ? fmt.date(date) : "—");
export const reportDateTime = (date: Date | null | undefined) => (date ? fmt.dateTime(date) : "—");

const roundTo = (value: number, digits: number) => Math.round(value * 10 ** digits) / 10 ** digits;

// A missing value reads "—" on a row and stays blank on an export's totals line.
export const textCell = (value: string | undefined, isTotal?: boolean) =>
  value || (isTotal ? "" : "—");
export const amountCell = (value: number | null | undefined, digits: number, isTotal?: boolean) =>
  value != null ? roundTo(value, digits) : isTotal ? "" : "—";

/** A sum that stays null when no value is set, so a totals line doesn't show a made-up 0. */
export const sumOf = (values: (number | null | undefined)[]) =>
  values.some((value) => value != null)
    ? values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
    : null;

/** A sortFn on a nullable number; missing values sort first. */
export const byNumber =
  <T>(pick: (row: T) => number | null | undefined) =>
  (a: T, b: T) =>
    (pick(a) ?? -1) - (pick(b) ?? -1);

/** Job rows by date, then pickup location, then id. */
export const byDateThenPickupLocation =
  <T extends { pickupLocationName: string; jobId: bigint }>(date: (row: T) => Date | null) =>
  (a: T, b: T) =>
    (date(a)?.getTime() ?? 0) - (date(b)?.getTime() ?? 0) ||
    a.pickupLocationName.localeCompare(b.pickupLocationName) ||
    compareIds(a.jobId, b.jobId);

// Display positions by row id, computed once per row model; the table re-renders a row whose
// position changes, so the numbers follow the on-screen sort.
const positions = new WeakMap<object, Map<string, number>>();

/** The running № column: the on-screen order, and the row order in the exports. */
export const numberColumn = <T extends object>(header: string, totalsLabel = ""): Column<T> => ({
  id: "number",
  header,
  accessor: (row, index) => ("isTotal" in row && row.isTotal ? totalsLabel : index + 1),
  width: 60,
  sortable: false,
  cell: ({ row, table }) => {
    const rows = table.getRowModel().rows;
    let byId = positions.get(rows);
    if (!byId) {
      byId = new Map(rows.map((r, index) => [r.id, index + 1]));
      positions.set(rows, byId);
    }
    return byId.get(row.id);
  },
});

/** Nominal volume: containers × the container type's volume; null without a container group. */
export const containerVolume = (job: ReportJob) =>
  job.numberOfContainers != null && job.containerTypeVolume != null
    ? job.numberOfContainers * job.containerTypeVolume
    : null;
